import { creditCreatedAt, creditField, creditFlowLabel, creditSignedChange, } from './creditLedgerUtils';
import { readEmbeddedUsdConversion } from './usdConversionDisplay';
function toNum(v) {
    if (v === undefined || v === null || v === '')
        return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}
function flowKey(row) {
    var _a, _b, _c;
    const f = creditFlowLabel(row);
    if (f === 'In')
        return 'in';
    if (f === 'Out')
        return 'out';
    const dir = String((_a = row.direction) !== null && _a !== void 0 ? _a : '').toLowerCase();
    if (dir === 'in')
        return 'in';
    if (dir === 'out')
        return 'out';
    const lt = String((_b = row.ledgerType) !== null && _b !== void 0 ? _b : '').toLowerCase();
    if (lt === 'deposit')
        return 'in';
    if (lt === 'withdraw')
        return 'out';
    const type = String((_c = row.type) !== null && _c !== void 0 ? _c : '').toLowerCase();
    if (type === 'credit')
        return 'in';
    if (type === 'debit')
        return 'out';
    return 'neutral';
}
function humanStatus(status) {
    const s = String(status || 'completed').toLowerCase();
    if (s === 'completed')
        return { label: 'Completed', tone: 'completed' };
    if (s === 'pending')
        return { label: 'Pending', tone: 'pending' };
    if (s === 'failed')
        return { label: 'Failed', tone: 'failed' };
    if (s === 'cancelled')
        return { label: 'Cancelled', tone: 'cancelled' };
    return { label: status || '—', tone: 'neutral' };
}
function formatCreditsAmount(n) {
    return `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })} credits`;
}
function isUsdConversionFlow(entry) {
    var _a, _b;
    const refType = String((_a = creditField(entry, 'referenceType', 'reference_type')) !== null && _a !== void 0 ? _a : '').toLowerCase();
    const desc = String((_b = entry.description) !== null && _b !== void 0 ? _b : '').toLowerCase();
    return (refType === 'usd_wallet_transaction' ||
        desc.includes('balance to credits') ||
        desc.includes('balance-to-credits'));
}
function isNonZero(v) {
    const n = toNum(v);
    return n != null && Math.abs(n) > 1e-9;
}
function counterpartyDisplay(entry) {
    var _a, _b;
    const cp = entry.counterparty;
    return ((_a = cp === null || cp === void 0 ? void 0 : cp.displayName) === null || _a === void 0 ? void 0 : _a.trim()) || ((_b = cp === null || cp === void 0 ? void 0 : cp.roleLabel) === null || _b === void 0 ? void 0 : _b.trim()) || 'Another account';
}
function partyDisplay(party) {
    var _a, _b, _c;
    return ((_a = party === null || party === void 0 ? void 0 : party.label) === null || _a === void 0 ? void 0 : _a.trim()) || ((_b = party === null || party === void 0 ? void 0 : party.displayName) === null || _b === void 0 ? void 0 : _b.trim()) || ((_c = party === null || party === void 0 ? void 0 : party.roleLabel) === null || _c === void 0 ? void 0 : _c.trim()) || null;
}
function hasMissingSource(entry) {
    const fromWalletId = creditField(entry, 'fromWalletId', 'from_wallet_id');
    return !entry.fromParty || !String(fromWalletId !== null && fromWalletId !== void 0 ? fromWalletId : '').trim();
}
function walletPhrase(audience) {
    return audience === 'operator' ? 'this wallet' : 'your wallet';
}
function buildExplanation(entry, flow, usdConversion, audience) {
    var _a;
    const wallet = walletPhrase(audience);
    if (usdConversion) {
        if (flow === 'out') {
            return audience === 'operator'
                ? 'This credit movement was created when USD balance was converted into credits. Credits from this wallet funded the credits added to the receiving account.'
                : 'This credit movement was created when USD balance was converted into credits. Your credits funded the credits added to the receiving account.';
        }
        if (flow === 'in') {
            return `Credits were added to ${wallet} after USD was converted. This credit movement was created when USD balance was converted into credits.`;
        }
        return 'This credit movement was created when USD balance was converted into credits.';
    }
    const type = String((_a = entry.type) !== null && _a !== void 0 ? _a : '').toLowerCase();
    if (type.includes('distribut')) {
        const network = audience === 'operator' ? 'the network' : 'your network';
        return flow === 'out'
            ? `Credits were sent to another account in ${network}.`
            : `Credits were received from another account in ${network}.`;
    }
    if (type.includes('withdraw')) {
        return flow === 'out'
            ? `Credits were withdrawn from ${wallet} and sent to another account.`
            : `Credits were returned or withdrawn into ${wallet}.`;
    }
    if (type.includes('deposit') || type.includes('purchase')) {
        return flow === 'in'
            ? `Credits were added to ${wallet} after a purchase or deposit.`
            : `Credits left ${wallet} as part of a purchase or funding activity.`;
    }
    if (flow === 'out')
        return `Credits left ${wallet} and were sent to another party.`;
    if (flow === 'in')
        return `Credits were added to ${wallet}.`;
    return audience === 'operator'
        ? 'This activity changed this credit wallet balance.'
        : 'This activity changed your credit wallet balance.';
}
function buildTitleSubtitle(flow, usdConversion, audience) {
    const wallet = walletPhrase(audience);
    if (usdConversion) {
        if (flow === 'out') {
            return {
                title: 'Credits funded from USD conversion',
                subtitle: 'Credits were used to fund a credit purchase for another account.',
            };
        }
        if (flow === 'in') {
            return {
                title: 'Credits added from USD conversion',
                subtitle: 'Credits were added after USD balance was converted.',
            };
        }
    }
    if (flow === 'out') {
        return { title: 'Credits sent', subtitle: `Credits left ${wallet}.` };
    }
    if (flow === 'in') {
        return { title: 'Credits received', subtitle: `Credits were added to ${wallet}.` };
    }
    return {
        title: 'Credit wallet activity',
        subtitle: audience === 'operator'
            ? 'Summary of how this activity affected this credit wallet.'
            : 'Summary of how this activity affected your credits.',
    };
}
function resolveUsdPricing(entry, magnitude, usdConversion, costAmount, costRate, conversionOverride) {
    var _a, _b, _c;
    if (usdConversion) {
        const embedded = readEmbeddedUsdConversion(entry, magnitude);
        const spent = (_a = conversionOverride === null || conversionOverride === void 0 ? void 0 : conversionOverride.usdSpent) !== null && _a !== void 0 ? _a : embedded === null || embedded === void 0 ? void 0 : embedded.usdSpent;
        const rate = (_c = (_b = conversionOverride === null || conversionOverride === void 0 ? void 0 : conversionOverride.ratePerCredit) !== null && _b !== void 0 ? _b : embedded === null || embedded === void 0 ? void 0 : embedded.ratePerCredit) !== null && _c !== void 0 ? _c : (spent != null && spent > 0 && magnitude > 0
            ? Number.parseFloat((spent / magnitude).toFixed(4))
            : null);
        if (spent != null && spent > 0) {
            return {
                usdValue: spent,
                ratePerCredit: rate !== null && rate !== void 0 ? rate : undefined,
                usdValueLabel: 'USD spent',
                rateLabel: 'Conversion rate',
            };
        }
    }
    return {
        usdValue: costAmount !== null && costAmount !== void 0 ? costAmount : undefined,
        ratePerCredit: costRate !== null && costRate !== void 0 ? costRate : undefined,
        usdValueLabel: 'USD value',
        rateLabel: 'Rate',
    };
}
/** Business-friendly read model for portal credit detail modal. */
export function buildCreditBusinessView(entry, accountLabel = 'Your account', conversionOverride, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone, audience = 'self') {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    const flow = flowKey(entry);
    const usdConversion = isUsdConversionFlow(entry);
    const { title, subtitle } = buildTitleSubtitle(flow, usdConversion, audience);
    const { label: statusLabel, tone: statusTone } = humanStatus(entry.status);
    const dateLabel = creditCreatedAt(entry, timezone);
    const signed = creditSignedChange(entry);
    const magnitude = Math.abs(signed);
    const amountHeading = flow === 'in' ? 'Credits received' : flow === 'out' ? 'Credits sent' : 'Credits changed';
    const costRate = toNum(creditField(entry, 'costRate', 'cost_rate'));
    const costAmount = toNum(creditField(entry, 'costAmount', 'cost_amount'));
    const platformFeeRate = toNum(creditField(entry, 'platformFeeRate', 'platform_fee_rate'));
    const platformFeeAmount = toNum(creditField(entry, 'platformFeeAmount', 'platform_fee_amount'));
    const cpName = counterpartyDisplay(entry);
    const fromPartyName = partyDisplay(entry.fromParty);
    const toPartyName = partyDisplay(entry.toParty);
    const desc = String((_a = entry.description) !== null && _a !== void 0 ? _a : '');
    const gameFromDesc = ((_c = (_b = desc.match(/withdraw(?:al)? from (.+?) game wallet/i)) === null || _b === void 0 ? void 0 : _b[1]) === null || _c === void 0 ? void 0 : _c.trim()) ||
        ((_e = (_d = desc.match(/deposit(?:ed)? to (.+?) game wallet/i)) === null || _d === void 0 ? void 0 : _d[1]) === null || _e === void 0 ? void 0 : _e.trim()) ||
        ((_g = (_f = desc.match(/(?:from|to) (.+?) game wallet/i)) === null || _f === void 0 ? void 0 : _f[1]) === null || _g === void 0 ? void 0 : _g.trim()) ||
        null;
    const gameProviderLabel = gameFromDesc ? `Game Provider: ${gameFromDesc}` : null;
    const effectiveFrom = fromPartyName ||
        (usdConversion && flow === 'in' && hasMissingSource(entry) ? 'Platform' : null) ||
        (flow === 'in' ? gameProviderLabel : null) ||
        (flow === 'in' ? cpName : null) ||
        (flow === 'out' ? accountLabel : null) ||
        '—';
    const effectiveTo = toPartyName ||
        (flow === 'out' ? gameProviderLabel : null) ||
        (flow === 'out' ? cpName : null) ||
        (flow === 'in' ? accountLabel : null) ||
        '—';
    const movement = flow === 'in'
        ? {
            from: { label: 'Received from', name: effectiveFrom },
            to: { label: 'Credit to', name: effectiveTo },
        }
        : flow === 'out'
            ? {
                from: { label: 'Paid from', name: effectiveFrom },
                to: { label: 'Sent to', name: effectiveTo },
            }
            : {
                from: { label: 'From', name: effectiveFrom },
                to: { label: 'To', name: effectiveTo },
            };
    const balanceBefore = toNum(creditField(entry, 'balanceBefore', 'balance_before'));
    const balanceAfter = toNum(creditField(entry, 'balanceAfter', 'balance_after'));
    const hasBefore = balanceBefore != null;
    const hasAfter = balanceAfter != null;
    let balanceNote;
    const balanceRows = [];
    if (!hasBefore && !hasAfter) {
        balanceNote = 'Balance snapshot is not available for this side of the transfer.';
    }
    else {
        if (hasBefore) {
            balanceRows.push({
                label: 'Credit balance before',
                value: balanceBefore.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 }),
            });
        }
        balanceRows.push({
            label: 'Credits changed',
            value: formatCreditsAmount(magnitude),
            emphasize: true,
        });
        if (hasAfter) {
            balanceRows.push({
                label: 'Credit balance after',
                value: balanceAfter.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 }),
            });
        }
    }
    const cp = entry.counterparty;
    const counterpartyId = (cp === null || cp === void 0 ? void 0 : cp.ownerType) && (cp === null || cp === void 0 ? void 0 : cp.ownerId) ? `${cp.ownerType} · ${cp.ownerId}` : '';
    const usdPricing = resolveUsdPricing(entry, magnitude, usdConversion, costAmount, costRate, conversionOverride);
    return {
        title,
        subtitle,
        flow,
        statusLabel,
        statusTone,
        dateLabel,
        amountHeading,
        creditsAmount: formatCreditsAmount(magnitude),
        creditsMagnitude: magnitude,
        ...usdPricing,
        platformFeeUsd: isNonZero(platformFeeAmount) ? platformFeeAmount : undefined,
        movement,
        explanation: buildExplanation(entry, flow, usdConversion, audience),
        balanceNote,
        balanceRows,
        relatedUsdCopy: usdConversion
            ? 'This credit transaction is linked to a USD wallet transaction.'
            : undefined,
        support: {
            ledgerId: String((_h = entry.id) !== null && _h !== void 0 ? _h : ''),
            transactionId: String((_j = creditField(entry, 'transactionId', 'transaction_id')) !== null && _j !== void 0 ? _j : ''),
            referenceType: String((_k = creditField(entry, 'referenceType', 'reference_type')) !== null && _k !== void 0 ? _k : '—'),
            referenceId: String((_l = creditField(entry, 'referenceId', 'reference_id')) !== null && _l !== void 0 ? _l : ''),
            rawType: String((_m = entry.type) !== null && _m !== void 0 ? _m : '—'),
            rawStatus: String((_o = entry.status) !== null && _o !== void 0 ? _o : '—'),
            rawDescription: String((_p = entry.description) !== null && _p !== void 0 ? _p : ''),
            counterpartyId,
        },
    };
}
